"use client"

import MuiTab from "@mui/material/Tab"
import MuiTabs from "@mui/material/Tabs"
import Typography from "@mui/material/Typography"
import Image from "next/image"
import { useCallback, useMemo, useRef, useState } from "react"
import type { components } from "umbraco"
import { getMediaUrl, resolveLinkHref } from "umbraco"
import { getNonUmbracoUrl, replaceRichTextLinks } from "utils"
import { getUmbracoMediaUrl } from "utils/getUmbracoMediaUrl"
import Container from "../../Container"
import GradientChevron from "../../icons/GradientChevron"
import ChevronLeft from "../../icons/ChevronLeft"
import ChevronRight from "../../icons/ChevronRight"
import { useIsDesktop } from "../../theme"
import {
  CardBack,
  CardBackDescription,
  CardBackLink,
  CardBackExternalLink,
  CardBackLinks,
  CardBackName,
  CardBackProfile,
  CardBackRole,
  CardBackTextGroup,
  CardBackThumbnail,
  CardFront,
  CardRotor,
  CardFrontInfo,
  CardFrontName,
  CardFrontText,
  CardGradientOverlay,
  CardGrid,
  CardImageContainer,
  CardWrapper,
  ChevronWrapper,
  EmptyState,
  HeaderSection,
  PaginationArrow,
  PaginationDot,
  PaginationDotOnly,
  PaginationWrapper,
  Root,
  Subtitle,
  Title,
  TitleRow,
} from "./styles"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
type PeopleCardsBlockData =
  components["schemas"]["PeopleCardsBlockPropertiesModel"]
type PersonCardItemData =
  components["schemas"]["PersonCardItemPropertiesModel"]
type ApiLinkModel = components["schemas"]["ApiLinkModel"]

interface Props {
  data: PeopleCardsBlockData
  settings?: Record<string, unknown> | null
  locale: string
  culture: string
  [key: string]: unknown
}

interface ParsedLink {
  url: string
  title: string
  target?: string
  isInternal: boolean
}

interface ParsedPerson {
  id: string
  imageUrl: string | null
  imageAltText: string | null
  name: string
  role: string
  descriptionMarkup: string
  categories: string[]
  links: ParsedLink[]
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const ALL_KEY = "__all__"
const DEFAULT_ITEMS_PER_PAGE = 9

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------
export default function PeopleCardsBlock({ data, culture }: Props) {
  const [activeCategory, setActiveCategory] = useState(ALL_KEY)
  const [currentPage, setCurrentPage] = useState(1)
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null)

  const isDesktop = useIsDesktop()
  const isRtl = culture === "he-il" || culture === "he-IL" || culture === "ar"
  const itemsPerPage = data.itemsPerPage ?? DEFAULT_ITEMS_PER_PAGE

  const rootRef = useRef<HTMLDivElement>(null)

  const enableCategories = (data as Record<string, unknown>).enableCategories as boolean | null | undefined
  const showCategories = enableCategories !== false

  const rawPaginationStyle = (data as Record<string, unknown>).paginationStyle
  const paginationStyle: string =
    Array.isArray(rawPaginationStyle)
      ? (rawPaginationStyle[0] as string) ?? "numbersWithArrows"
      : typeof rawPaginationStyle === "string"
        ? rawPaginationStyle
        : "numbersWithArrows"

  // Parse people items from CMS Block List
  const allPeople: ParsedPerson[] = useMemo(() => {
    const items = (data.items as { items?: Array<{ content: { id?: string; properties?: PersonCardItemData } }> })?.items ?? []
    return items.map((block, index) => {
      const props = block.content.properties
      const imageRel = getMediaUrl(props?.image as unknown)
      const imageAltText = props?.imageAltText
      const rawDesc = props?.description
      const rawMarkup =
        rawDesc && typeof rawDesc === "object" && "markup" in rawDesc
          ? (rawDesc as { markup: string }).markup
          : typeof rawDesc === "string"
            ? rawDesc
            : ""
      const descriptionMarkup = replaceRichTextLinks(rawMarkup)

      const rawLinks = (props?.links ?? []) as ApiLinkModel[]
      const parsedLinks: ParsedLink[] = []
      for (const link of rawLinks) {
        const resolved = resolveLinkHref([link], {
          getNonUmbracoUrl,
          getUmbracoMediaUrl,
        })
        if (!resolved) continue
        parsedLinks.push({
          url: resolved.href,
          title: link.title ?? resolved.href,
          target: resolved.target,
          isInternal: resolved.isInternal,
        })
      }

      return {
        id: block.content.id ?? `person-${index}`,
        imageUrl: imageRel ? getUmbracoMediaUrl(imageRel) : null,
        imageAltText: imageAltText || null,
        name: props?.personName ?? "",
        role: props?.role ?? "",
        descriptionMarkup,
        categories: props?.personCategories ?? [],
        links: parsedLinks,
      }
    })
  }, [data.items])

  // Use only CMS-level categories — do NOT fallback to person item categories
  const cmsCategories = data.categories ?? []
  const hasCategories = showCategories && cmsCategories.length > 0

  // Filter by active category
  const filteredPeople = useMemo(() => {
    if (activeCategory === ALL_KEY) return allPeople
    return allPeople.filter((p) => p.categories.includes(activeCategory))
  }, [allPeople, activeCategory])

  // Pagination
  const totalPages = Math.ceil(filteredPeople.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const visiblePeople = filteredPeople.slice(
    startIndex,
    startIndex + itemsPerPage,
  )

  const allLabel = isRtl ? "הכל" : "All"
  const noResultsLabel = isRtl ? "לא נמצאו תוצאות" : "No results"

  // Handlers
  const handleCategoryChange = useCallback(
    (_: unknown, value: string) => {
      setActiveCategory(value)
      setCurrentPage(1)
      setExpandedIndex(null)
    },
    [],
  )

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page)
    setHoveredIndex(null)
    setExpandedIndex(null)
    setTimeout(() => {
      rootRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
    }, 0)
  }, [])

  const handlePrevPage = useCallback(() => {
    setCurrentPage((prev) => {
      const next = Math.max(1, prev - 1)
      return next
    })
    setHoveredIndex(null)
    setExpandedIndex(null)
    setTimeout(() => {
      rootRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
    }, 0)
  }, [])

  const handleNextPage = useCallback(() => {
    setCurrentPage((prev) => {
      const next = Math.min(totalPages, prev + 1)
      return next
    })
    setHoveredIndex(null)
    setExpandedIndex(null)
    setTimeout(() => {
      rootRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
    }, 0)
  }, [totalPages])

  // RTL-aware arrow handlers
  const handleLeftArrow = isRtl ? handleNextPage : handlePrevPage
  const handleRightArrow = isRtl ? handlePrevPage : handleNextPage
  const leftDisabled = isRtl ? currentPage >= totalPages : currentPage <= 1
  const rightDisabled = isRtl ? currentPage <= 1 : currentPage >= totalPages

  const handleMouseEnter = useCallback((e: React.MouseEvent<HTMLElement>) => {
    const idx = Number(e.currentTarget.dataset.index)
    if (!Number.isNaN(idx)) setHoveredIndex(idx)
  }, [])

  const handleMouseLeave = useCallback(() => setHoveredIndex(null), [])

  const handleCardInteraction = useCallback(
    (index: number) => {
      if (isDesktop) return
      setExpandedIndex((prev) => (prev === index ? null : index))
    },
    [isDesktop],
  )

  const showArrows = paginationStyle === "numbersWithArrows"
  const useDots = paginationStyle === "dots"

  return (
    <Container paddingMobile>
      <Root ref={rootRef} data-block-type="peopleCardsBlock">
        {/* Title Section */}
        <HeaderSection>
          <TitleRow>
            <ChevronWrapper>
              <GradientChevron width={17} height={27} />
            </ChevronWrapper>
            <Title variant="h2">{data.sectionTitle}</Title>
          </TitleRow>
          {data.sectionSubtitle && (
            <Subtitle variant="body1">{data.sectionSubtitle}</Subtitle>
          )}
        </HeaderSection>

        {/* Category Tabs */}
        {hasCategories && (
          <MuiTabs
            value={activeCategory}
            onChange={handleCategoryChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
          >
            <MuiTab label={allLabel} value={ALL_KEY} />
            {cmsCategories.map((cat) => (
              <MuiTab key={cat} label={cat} value={cat} />
            ))}
          </MuiTabs>
        )}

        {/* Card Grid or Empty State */}
        {visiblePeople.length === 0 ? (
          <EmptyState>
            <Typography variant="body1" color="textSecondary">
              {noResultsLabel}
            </Typography>
          </EmptyState>
        ) : (
          <CardGrid>
            {visiblePeople.map((person, index) => {
              const flipped = isDesktop ? hoveredIndex === index : expandedIndex === index
              return (
                <CardWrapper
                  key={person.id}
                  data-index={index}
                  onMouseEnter={isDesktop ? handleMouseEnter : undefined}
                  onMouseLeave={isDesktop ? handleMouseLeave : undefined}
                  onClick={() => handleCardInteraction(index)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault()
                      handleCardInteraction(index)
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label={`${person.name}, ${person.role}`}
                >
                  {/* EGDE-519: rotor spins the two faces as one 3D card */}
                  <CardRotor $flipped={flipped}>
                  {/* Front: image + gradient + name/role */}
                  <CardFront $visible={!flipped}>
                    <CardImageContainer>
                      {person.imageUrl && (
                        <Image
                          src={person.imageUrl}
                          alt={person.imageAltText || person.name}
                          fill
                          sizes="(max-width: 767px) 100vw, (max-width: 1279px) 50vw, 383px"
                          style={{ objectFit: "cover" }}
                        />
                      )}
                    </CardImageContainer>
                    <CardGradientOverlay />
                    <CardFrontInfo>
                      <CardFrontName variant="body1">
                        {person.name}
                      </CardFrontName>
                      <CardFrontText variant="body1">
                        {person.role}
                      </CardFrontText>
                    </CardFrontInfo>
                  </CardFront>

                  {/* Back: white card with thumbnail + description + links */}
                  <CardBack $visible={flipped}>
                    <CardBackProfile>
                      <CardBackThumbnail>
                        {person.imageUrl && (
                          <Image
                            src={person.imageUrl}
                            alt={person.imageAltText || person.name}
                            fill
                            sizes="86px"
                            style={{ objectFit: "cover" }}
                          />
                        )}
                      </CardBackThumbnail>
                      <CardBackTextGroup>
                        <CardBackName variant="body1">
                          {person.name}
                        </CardBackName>
                        <CardBackRole variant="body1">
                          {person.role}
                        </CardBackRole>
                      </CardBackTextGroup>
                    </CardBackProfile>
                    {person.descriptionMarkup && (
                      <CardBackDescription
                        dangerouslySetInnerHTML={{ __html: person.descriptionMarkup }}
                      />
                    )}
                    {person.links.length > 0 && (
                      <CardBackLinks>
                        {person.links.map((link, linkIdx) =>
                          link.isInternal ? (
                            <CardBackLink
                              key={linkIdx}
                              href={link.url}
                              target={link.target}
                              rel={link.target === "_blank" ? "noopener noreferrer" : undefined}
                              onClick={(e: React.MouseEvent) => e.stopPropagation()}
                            >
                              {link.title}
                            </CardBackLink>
                          ) : (
                            <CardBackExternalLink
                              key={linkIdx}
                              href={link.url}
                              target={link.target ?? "_blank"}
                              rel="noopener noreferrer"
                              onClick={(e: React.MouseEvent) => e.stopPropagation()}
                            >
                              {link.title}
                            </CardBackExternalLink>
                          ),
                        )}
                      </CardBackLinks>
                    )}
                  </CardBack>
                  </CardRotor>
                </CardWrapper>
              )
            })}
          </CardGrid>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <PaginationWrapper>
            {showArrows && (
              <PaginationArrow
                onClick={handleRightArrow}
                disabled={rightDisabled}
                aria-label="Previous page"
              >
                <ChevronRight />
              </PaginationArrow>
            )}
            {useDots
              ? Array.from({ length: totalPages }, (_, i) => i + 1).map(
                  (page) => (
                    <PaginationDotOnly
                      key={page}
                      $active={page === currentPage}
                      onClick={() => handlePageChange(page)}
                      aria-label={`Page ${page}`}
                      aria-current={page === currentPage ? "page" : undefined}
                    />
                  ),
                )
              : Array.from({ length: totalPages }, (_, i) => i + 1).map(
                  (page) => (
                    <PaginationDot
                      key={page}
                      $active={page === currentPage}
                      onClick={() => handlePageChange(page)}
                      aria-label={`Page ${page}`}
                      aria-current={page === currentPage ? "page" : undefined}
                    >
                      {page}
                    </PaginationDot>
                  ),
                )}
            {showArrows && (
              <PaginationArrow
                onClick={handleLeftArrow}
                disabled={leftDisabled}
                aria-label="Next page"
              >
                <ChevronLeft />
              </PaginationArrow>
            )}
          </PaginationWrapper>
        )}
      </Root>
    </Container>
  )
}
